import { JSX, useState } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableRow,
    TableCell,
    TableHead,
    Collapse,
    IconButton,
    Typography
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { TokenHistoryTiming, TokenHistoryTimingNode } from "../../../../types/dashboard";
import {
    numeric,
    eyebrow,
    series,
    formatTokens,
    formatClock,
    formatDuration
} from "../styles/dashboard-tokens";
import { timingDuration, timingTokens } from "../timing-metrics";

type TokenHistoryTimingTableRowProps = {
    row: TokenHistoryTiming,
    /** Longest run on this page, so every bar is scaled against the same reference. */
    slowest: number,
}

export const TokenHistoryTimingTableRow: (props: TokenHistoryTimingTableRowProps) => JSX.Element =
    (props: TokenHistoryTimingTableRowProps): JSX.Element => {
    const rowData: TokenHistoryTiming = props.row;
    const [open, setOpen] = useState<boolean>(false);

    let workflow: JSX.Element = (
        <Typography component="span" variant="body2" color="text.disabled">{ "—" }</Typography>
    );
    if (rowData.workflow !== '') {
        workflow = (
            // Router link rather than a bare href: HQ uses hash routing, so "/workflows/x" as a
            // plain URL never resolved to the workflow.
            <Typography
                component={ RouterLink }
                to={ "/workflows/" + rowData.workflow }
                variant="body2"
                sx={ { color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } } }
            >
                { rowData.workflow_name ?? rowData.workflow }
            </Typography>
        );
    }

    // The card is titled "today", so the date on every row was 10 characters of noise.
    let clock: string = '—';
    if (rowData.created !== undefined) {
        clock = formatClock(new Date(rowData.created.date));
    }

    const duration: number | null = timingDuration(rowData);
    const barWidth: number = (duration !== null && props.slowest > 0)
        ? Math.max(2, (duration / props.slowest) * 100)
        : 0;

    return (
        <>
            <TableRow
                sx={ {
                    '& > *': { borderBottom: 'unset' },
                    '&:hover': { bgcolor: 'action.hover' },
                } }
            >
                <TableCell sx={ { width: 44, pr: 0 } }>
                    <IconButton
                        aria-label={ open ? "Hide node breakdown" : "Show node breakdown" }
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                    </IconButton>
                </TableCell>
                <TableCell sx={ { ...numeric, width: 92, color: 'text.secondary', fontSize: 13 } }>
                    { clock }
                </TableCell>
                <TableCell sx={ { textTransform: 'capitalize', fontWeight: 500 } }>
                    { rowData.type.replaceAll('_', ' ') }
                </TableCell>
                <TableCell sx={ { maxWidth: 220 } }>
                    { workflow }
                </TableCell>
                { /* The signature of this card: each run's wall-clock time drawn against the
                     slowest on the page, so a slow agent is visible without reading a number. */ }
                <TableCell sx={ { width: 200 } }>
                    <Box sx={ { display: 'flex', alignItems: 'center', gap: 1.25 } }>
                        <Box
                            sx={ {
                                flex: 1,
                                height: 6,
                                minWidth: 60,
                                borderRadius: 3,
                                bgcolor: 'action.hover',
                                overflow: 'hidden',
                            } }
                        >
                            <Box
                                sx={ {
                                    width: barWidth + '%',
                                    height: '100%',
                                    borderRadius: 3,
                                    bgcolor: series.request,
                                } }
                            />
                        </Box>
                        <Typography
                            component="span"
                            sx={ { ...numeric, fontSize: 13, minWidth: 56, textAlign: 'right' } }
                        >
                            { duration === null ? '—' : formatDuration(duration) }
                        </Typography>
                    </Box>
                </TableCell>
                <TableCell align="right" sx={ { ...numeric, width: 110, fontSize: 13 } }>
                    { formatTokens(timingTokens(rowData)) }
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ my: 1.5, ml: 5.5, mr: 1, borderLeft: 2, borderColor: 'divider', pl: 2 }}>
                            <Typography component="p" sx={ { ...eyebrow, mb: 1 } }>
                                { "Node breakdown" }
                            </Typography>
                            <Table size="small" aria-label="node breakdown">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={ eyebrow }>{ "Node" }</TableCell>
                                        <TableCell sx={ eyebrow } align="right">{ "Request" }</TableCell>
                                        <TableCell sx={ eyebrow } align="right">{ "Response" }</TableCell>
                                        <TableCell sx={ eyebrow } align="right">{ "Tokens/sec" }</TableCell>
                                        <TableCell sx={ eyebrow } align="right">{ "Duration" }</TableCell>
                                        <TableCell sx={ eyebrow } align="right">{ "Requests" }</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    { rowData?.data?.map((datum: TokenHistoryTimingNode, index: number) => {
                                        return (
                                            <TableRow key={ "collapsible_table_row_" + index }>
                                                <TableCell sx={ { fontSize: 13 } }>{ datum.node_id ?? 'N/A' }</TableCell>
                                                <TableCell sx={ { ...numeric, fontSize: 13 } } align="right">{ formatTokens(datum.request) }</TableCell>
                                                <TableCell sx={ { ...numeric, fontSize: 13 } } align="right">{ formatTokens(datum.response) }</TableCell>
                                                <TableCell sx={ { ...numeric, fontSize: 13 } } align="right">{ datum?.tokens_per_second?.toFixed(2) ?? 'N/A' }</TableCell>
                                                <TableCell sx={ { ...numeric, fontSize: 13 } } align="right">{ datum?.duration !== undefined ? formatDuration(datum.duration) : 'N/A' }</TableCell>
                                                <TableCell sx={ { ...numeric, fontSize: 13 } } align="right">{ datum.requests ?? 'N/A' }</TableCell>
                                            </TableRow>
                                        );
                                    } ) }
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}
